import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, TrendingUp, Shield, ShieldCheck, Gift, CheckCircle, Users, ShoppingCart, Coins, ArrowRight, ArrowDownLeft, ArrowUpRight, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useGoldRate } from '../../context/GoldRateContext';
import { useProfileDrawer } from '../../context/ProfileDrawerContext';
import api from '../../services/api';

const HeroSection = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { buyRate, sellRate, rate22kt, loading: rateLoading } = useGoldRate();
  const { openDrawer } = useProfileDrawer();
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);

  useEffect(() => {
    // Only fetch if authenticated
    if (!isAuthenticated) return;

    const fetchRecentTransactions = async () => {
      try {
        setTransactionsLoading(true);
        const response = await api.get('/transactions?limit=5');
        setRecentTransactions(response.data.transactions || []);
      } catch (error) {
        console.error('Error fetching recent transactions:', error);
      } finally {
        setTransactionsLoading(false);
      }
    };

    fetchRecentTransactions();
  }, [isAuthenticated]);

  const milliBalance = user?.milli_balance || 0;

  // Format date for recent activity
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  // Get transaction type display info (considers both type and status)
  const getTransactionInfo = (type, status = 'completed') => {
    // If failed, show red styling with alert icon
    if (status === 'failed') {
      return { 
        label: 'Failed', 
        icon: AlertTriangle, 
        color: 'text-red-600', 
        iconColor: 'text-red-500', 
        bgColor: 'bg-red-100', 
        prefix: '', 
        valueColor: 'text-red-500' 
      };
    }
    
    // If pending, show amber styling
    if (status === 'pending' || status === 'processing') {
      return { 
        label: 'Pending', 
        icon: Wallet, 
        color: 'text-amber-600', 
        iconColor: 'text-amber-500', 
        bgColor: 'bg-amber-100', 
        prefix: '', 
        valueColor: 'text-amber-500' 
      };
    }
    
    // Success states by type
    switch(type) {
      case 'buy':
        return { label: 'Booked', icon: ArrowDownLeft, color: 'text-[#004D40]', iconColor: 'text-[#22C55E]', bgColor: 'bg-[#22C55E]/10', prefix: '+', valueColor: 'text-[#22C55E]' };
      case 'sell':
        return { label: 'Refunded', icon: ArrowUpRight, color: 'text-[#B8860B]', iconColor: 'text-[#B8860B]', bgColor: 'bg-[#B8860B]/10', prefix: '-', valueColor: 'text-[#B8860B]' };
      case 'redeem':
        return { label: 'Redeemed', icon: Gift, color: 'text-purple-600', iconColor: 'text-purple-600', bgColor: 'bg-purple-100', prefix: '-', valueColor: 'text-purple-600' };
      case 'referral':
      case 'referral_reward':
      case 'referral_bonus':
        return { label: 'Referral', icon: Users, color: 'text-[#004D40]', iconColor: 'text-[#22C55E]', bgColor: 'bg-[#22C55E]/10', prefix: '+', valueColor: 'text-[#22C55E]' };
      case 'gift_sent':
        return { label: 'Gift Sent', icon: Gift, color: 'text-pink-600', iconColor: 'text-pink-500', bgColor: 'bg-pink-100', prefix: '-', valueColor: 'text-pink-500' };
      case 'gift_received':
        return { label: 'Gift Received', icon: Gift, color: 'text-[#004D40]', iconColor: 'text-[#22C55E]', bgColor: 'bg-[#22C55E]/10', prefix: '+', valueColor: 'text-[#22C55E]' };
      case 'gift':
        // Check milli_amount to determine if sent or received
        return { label: 'Gift', icon: Gift, color: 'text-pink-600', iconColor: 'text-pink-500', bgColor: 'bg-pink-100', prefix: '', valueColor: 'text-pink-500' };
      default:
        return { label: type, icon: Wallet, color: 'text-gray-600', iconColor: 'text-gray-600', bgColor: 'bg-gray-100', prefix: '', valueColor: 'text-gray-600' };
    }
  };

  return (
    <section 
      className="relative overflow-hidden mx-2 sm:mx-4 mt-2 sm:mt-4 rounded-xl pb-28 sm:pb-36 md:pb-40"
      style={{
        width: 'calc(100vw - 1rem)',
        height: 'auto',
        minHeight: '380px',
      }}
    >
      {/* Content */}
      <div className="relative w-full h-full px-3 sm:px-6 lg:px-8 pt-2 sm:pt-3 pb-3 sm:pb-4 flex flex-col lg:flex-row">
        {/* Left Side - Why Save with Milli (Hidden on mobile, visible on lg+, ONLY for logged out users) */}
        {!isAuthenticated && (
          <div className="hidden lg:flex items-start pt-4 z-10 mr-4 xl:mr-6 flex-shrink-0">
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-3 xl:p-5 w-[150px] xl:w-[180px] border-2 border-[#B8860B]/30">
              <h3 className="text-sm xl:text-base font-semibold text-[#8B6914] mb-3 xl:mb-4 text-center">Why Save with <span className="font-bold text-[#996515] text-lg xl:text-xl">Milli</span>?</h3>
              <div className="flex flex-col gap-3 xl:gap-4">
                {/* 22K Purity */}
                <div className="flex flex-col items-center text-center">
                  <div className="w-8 h-8 xl:w-10 xl:h-10 rounded-full bg-[#B8860B]/15 border border-[#B8860B]/30 flex items-center justify-center mb-1 xl:mb-2">
                    <ShieldCheck className="w-4 h-4 xl:w-5 xl:h-5 text-[#996515]" />
                  </div>
                  <p className="text-xs xl:text-sm font-medium text-[#8B6914]">22K Purity</p>
                  <p className="text-[10px] xl:text-xs text-[#A67C00]">Assured purity</p>
                </div>
                
                {/* Secure & Safe */}
                <div className="flex flex-col items-center text-center">
                  <div className="w-8 h-8 xl:w-10 xl:h-10 rounded-full bg-[#B8860B]/15 border border-[#B8860B]/30 flex items-center justify-center mb-1 xl:mb-2">
                    <Shield className="w-4 h-4 xl:w-5 xl:h-5 text-[#996515]" />
                  </div>
                  <p className="text-xs xl:text-sm font-medium text-[#8B6914]">Secure & Safe</p>
                  <p className="text-[10px] xl:text-xs text-[#A67C00]">Bank-level security</p>
                </div>
                
                {/* No Hidden Charges */}
                <div className="flex flex-col items-center text-center">
                  <div className="w-8 h-8 xl:w-10 xl:h-10 rounded-full bg-[#B8860B]/15 border border-[#B8860B]/30 flex items-center justify-center mb-1 xl:mb-2">
                    <Gift className="w-4 h-4 xl:w-5 xl:h-5 text-[#996515]" />
                  </div>
                  <p className="text-xs xl:text-sm font-medium text-[#8B6914]">No Hidden Charges</p>
                  <p className="text-[10px] xl:text-xs text-[#A67C00]">Transparent pricing</p>
                </div>
                
                {/* Flexible Savings */}
                <div className="flex flex-col items-center text-center">
                  <div className="w-8 h-8 xl:w-10 xl:h-10 rounded-full bg-[#B8860B]/15 border border-[#B8860B]/30 flex items-center justify-center mb-1 xl:mb-2">
                    <CheckCircle className="w-4 h-4 xl:w-5 xl:h-5 text-[#996515]" />
                  </div>
                  <p className="text-xs xl:text-sm font-medium text-[#8B6914]">Flexible Savings</p>
                  <p className="text-[10px] xl:text-xs text-[#A67C00]">Save any amount</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Right Side - Main content */}
        <div className="flex-1 flex flex-col z-10 min-w-0">
          {/* Main Heading Section */}
          <div className="flex flex-col ml-0 mt-2 sm:mt-4 lg:mt-6">
            {/* Conditional Header: Welcome back (logged in) OR Tagline (logged out) */}
            {isAuthenticated ? (
              <>
                <p className="text-sm sm:text-base md:text-lg text-[#B8860B] font-medium mb-1">
                  Welcome back,
                </p>
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal leading-tight mb-4 sm:mb-6 tracking-wide text-[#00302B]" style={{ fontFamily: "Georgia, serif" }}>
                  {user?.name || 'User'}
                </h1>
              </>
            ) : (
              <>
                {/* Tagline - directly above heading */}
                <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-semibold text-[#B8860B] tracking-wide sm:tracking-widest uppercase mb-2 sm:mb-3">
                  Wealth Woven, Wisely
                </p>
                
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-normal leading-tight mb-3 sm:mb-4 tracking-wide text-[#00302B]" style={{ fontFamily: "Georgia, serif" }}>
                  WITH<br />MINT A MILLI
                </h1>
                <p className="text-gray-600 text-xs sm:text-sm md:text-base lg:text-lg mb-5 sm:mb-6 lg:mb-8 leading-relaxed max-w-2xl">
                  Start your journey with just ₹100.
                </p>
                
                {/* CTA Button - only for logged out users */}
                <div className="flex gap-3 sm:gap-4 mb-5 sm:mb-6 lg:mb-8">
                  <button 
                    onClick={() => openDrawer()}
                    className="px-5 py-2.5 sm:px-6 sm:py-3 lg:px-8 lg:py-3.5 bg-gradient-to-r from-[#00302B] to-[#004D40] ring-2 ring-[#004D40]/30 text-sm sm:text-base lg:text-lg font-semibold rounded-lg shadow-md hover:shadow-lg hover:scale-105 transition-all cursor-pointer whitespace-nowrap text-[#FFD700]"
                  >
                    Log In / Register
                  </button>
                </div>
              </>
            )}
            
            {/* Stats Row */}
            <div className={`grid gap-3 sm:gap-4 ${isAuthenticated ? 'grid-cols-1 sm:grid-cols-[1.2fr_0.8fr] max-w-[400px] sm:max-w-[580px] lg:max-w-[620px]' : 'grid-cols-1 max-w-[140px] sm:max-w-[200px] lg:max-w-[250px]'}`}>
              {/* Milli Balance Card - Only show when logged in (Dark Green Theme with background image) */}
              {isAuthenticated && (
                <div 
                  className="p-5 sm:p-6 rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.15),0_2px_8px_rgba(0,0,0,0.1)] min-h-[130px] sm:min-h-[150px] relative overflow-hidden"
                  style={{
                    backgroundImage: 'url(/assets-new/balance-card-bg.webp)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <div className="flex items-start justify-between h-full relative z-10">
                    {/* Left side - Balance info */}
                    <div>
                      <p className="text-white/70 text-sm sm:text-base font-medium mb-2">Your Milli Balance</p>
                      <p className="text-4xl sm:text-5xl font-bold text-white">
                        {milliBalance.toFixed(2)} <span className="text-lg sm:text-xl font-normal text-white/80">Milli&apos;s</span>
                      </p>
                      <p className="text-sm sm:text-base text-white/60 mt-1">
                        ≈ {(milliBalance / 1000).toFixed(4)} grams
                      </p>
                      {/* Current value in INR */}
                      {sellRate > 0 && (
                        <p className="text-base sm:text-lg text-[#FFD700] font-semibold mt-2">
                          ≈ ₹{(milliBalance * sellRate).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </p>
                      )}
                    </div>
                    {/* Right side - Refer & Earn button */}
                    <button 
                      onClick={() => openDrawer('referral')}
                      className="flex flex-col items-center gap-1.5 p-2.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                    >
                      <Users className="w-7 h-7 text-[#FFD700]" />
                      <span className="text-xs sm:text-sm text-[#FFD700] font-medium whitespace-nowrap">Refer &<br/>Earn</span>
                    </button>
                  </div>
                </div>
              )}
              
              {/* Live Booking Rate Card */}
              <div className="p-4 sm:p-5 rounded-xl border border-gray-200 bg-white/90 backdrop-blur-sm shadow-[0_4px_20px_rgba(0,0,0,0.08),0_2px_8px_rgba(0,0,0,0.05)] min-h-[130px] sm:min-h-[150px]">
                <div className="flex items-center gap-3 h-full">
                  {/* Trending Icon */}
                  <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#004D40]/10 border border-[#004D40]/20 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-[#004D40]" />
                  </div>
                  {/* Rate Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs sm:text-sm text-gray-600 font-medium">Live Booking Rate</span>
                      <span className="px-1.5 py-0.5 bg-[#004D40] text-white text-[8px] sm:text-[10px] font-medium rounded">Live</span>
                    </div>
                    {rateLoading ? (
                      <div className="animate-pulse">
                        <div className="h-7 sm:h-8 bg-gray-200 rounded w-24 mb-1"></div>
                        <div className="h-4 bg-gray-200 rounded w-20"></div>
                      </div>
                    ) : (
                      <>
                        <p className="text-xl sm:text-2xl font-bold text-gray-900">
                          ₹{buyRate.toFixed(2)} <span className="text-sm font-normal text-gray-500">/ Milli</span>
                        </p>
                        <div className="border-t border-gray-200 mt-2 pt-2">
                          <p className="text-xs sm:text-sm text-gray-500">
                            22KT: ₹{rate22kt.toLocaleString('en-IN')} / g
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Quick Actions Row - Only for logged in users */}
          {isAuthenticated && (
            <div className="mt-4 sm:mt-5">
              <div className="bg-white/90 backdrop-blur-sm rounded-xl border-2 border-[#B8860B]/30 shadow-[0_4px_20px_rgba(0,0,0,0.08),0_2px_8px_rgba(0,0,0,0.05)] p-3 sm:p-4 max-w-[400px] sm:max-w-[580px] lg:max-w-[620px]">
                <div className="grid grid-cols-3 divide-x divide-gray-200">
                  {/* Book Gold */}
                  <button 
                    onClick={() => navigate('/book-gold')}
                    className="flex items-center justify-center gap-2 sm:gap-3 px-2 sm:px-4 py-2 hover:bg-gray-50 transition-colors group"
                  >
                    <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-[#B8860B]/10 border-2 border-[#B8860B]/40 flex items-center justify-center flex-shrink-0 group-hover:border-[#B8860B] transition-colors">
                      <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5 text-[#B8860B]" />
                    </div>
                    <p className="text-sm sm:text-base font-semibold text-[#004D40]">Book Gold</p>
                  </button>
                  
                  {/* Refund Gold */}
                  <button 
                    onClick={() => navigate('/refund-gold')}
                    className="flex items-center justify-center gap-2 sm:gap-3 px-2 sm:px-4 py-2 hover:bg-gray-50 transition-colors group"
                  >
                    <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-[#B8860B]/10 border-2 border-[#B8860B]/40 flex items-center justify-center flex-shrink-0 group-hover:border-[#B8860B] transition-colors">
                      <Coins className="w-4 h-4 sm:w-5 sm:h-5 text-[#B8860B]" />
                    </div>
                    <p className="text-sm sm:text-base font-semibold text-[#B8860B]">Refund Gold</p>
                  </button>
                  
                  {/* Redeem Gold */}
                  <button 
                    onClick={() => navigate('/redeem-gold')}
                    className="flex items-center justify-center gap-2 sm:gap-3 px-2 sm:px-4 py-2 hover:bg-gray-50 transition-colors group"
                  >
                    <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-[#B8860B]/10 border-2 border-[#B8860B]/40 flex items-center justify-center flex-shrink-0 group-hover:border-[#B8860B] transition-colors">
                      <Gift className="w-4 h-4 sm:w-5 sm:h-5 text-[#B8860B]" />
                    </div>
                    <p className="text-sm sm:text-base font-semibold text-[#B8860B]">Redeem Gold</p>
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Recent Activity Section - Only for logged in users */}
          {isAuthenticated && (
            <div className="mt-4 sm:mt-5 max-w-[400px] sm:max-w-[580px] lg:max-w-[620px]">
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base sm:text-lg font-semibold text-[#00302B]" style={{ fontFamily: 'Georgia, serif' }}>
                  Recent Activity
                </h3>
                <button 
                  onClick={() => navigate('/transaction-history')}
                  className="flex items-center gap-1 text-sm text-[#B8860B] hover:text-[#996515] transition-colors font-medium"
                >
                  View All
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              
              {/* Horizontal scroll list */}
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide -mx-1 px-1">
                {transactionsLoading ? (
                  // Loading skeleton
                  <>
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex-shrink-0 bg-white/90 backdrop-blur-sm rounded-xl p-3 min-w-[160px] border border-gray-200 shadow-sm animate-pulse">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 rounded-full bg-gray-200"></div>
                          <div className="h-4 bg-gray-200 rounded w-16"></div>
                        </div>
                        <div className="h-3 bg-gray-200 rounded w-14 mb-2"></div>
                        <div className="h-5 bg-gray-200 rounded w-24"></div>
                      </div>
                    ))}
                  </>
                ) : recentTransactions.length === 0 ? (
                  // Empty state
                  <div className="flex-shrink-0 bg-white/90 backdrop-blur-sm rounded-xl p-4 min-w-[200px] border border-gray-200 shadow-sm text-center">
                    <Wallet className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No transactions yet</p>
                    <button 
                      onClick={() => navigate('/book-gold')}
                      className="mt-2 text-xs text-[#004D40] font-medium hover:underline"
                    >
                      Book your first gold
                    </button>
                  </div>
                ) : (
                  // Transaction items
                  recentTransactions.map((tx) => {
                    const txInfo = getTransactionInfo(tx.type, tx.status);
                    const IconComponent = txInfo.icon;
                    return (
                      <div 
                        key={tx._id || tx.id} 
                        className="flex-shrink-0 bg-white/90 backdrop-blur-sm rounded-xl p-3 min-w-[160px] border border-gray-200 shadow-[0_2px_10px_rgba(0,0,0,0.05)] hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => navigate('/transaction-history')}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`w-8 h-8 rounded-full ${txInfo.bgColor} flex items-center justify-center`}>
                            <IconComponent className={`w-4 h-4 ${txInfo.iconColor}`} />
                          </div>
                          <p className={`text-sm font-medium ${txInfo.color}`}>{txInfo.label}</p>
                        </div>
                        <p className="text-xs text-gray-500 mb-1">{formatDate(tx.created_at)}</p>
                        <p className={`text-base font-bold ${txInfo.valueColor}`}>
                          {txInfo.prefix}{tx.milli_amount?.toFixed(2) || '0.00'} Milli&apos;s
                        </p>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
          
          {/* Mobile: Why Save with Milli (horizontal scroll) - ONLY for logged out users */}
          {!isAuthenticated && (
            <div className="lg:hidden mt-4 -mx-3 sm:-mx-6 px-3 sm:px-6">
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                <div className="flex-shrink-0 bg-white/70 backdrop-blur-sm rounded-xl p-3 min-w-[120px] border border-[#B8860B]/30 text-center">
                  <div className="w-8 h-8 rounded-full bg-[#B8860B]/15 border border-[#B8860B]/30 flex items-center justify-center mb-1 mx-auto">
                    <ShieldCheck className="w-4 h-4 text-[#996515]" />
                  </div>
                  <p className="text-xs font-medium text-[#8B6914]">22K Purity</p>
                </div>
                <div className="flex-shrink-0 bg-white/70 backdrop-blur-sm rounded-xl p-3 min-w-[120px] border border-[#B8860B]/30 text-center">
                  <div className="w-8 h-8 rounded-full bg-[#B8860B]/15 border border-[#B8860B]/30 flex items-center justify-center mb-1 mx-auto">
                    <Shield className="w-4 h-4 text-[#996515]" />
                  </div>
                  <p className="text-xs font-medium text-[#8B6914]">Secure & Safe</p>
                </div>
                <div className="flex-shrink-0 bg-white/70 backdrop-blur-sm rounded-xl p-3 min-w-[120px] border border-[#B8860B]/30 text-center">
                  <div className="w-8 h-8 rounded-full bg-[#B8860B]/15 border border-[#B8860B]/30 flex items-center justify-center mb-1 mx-auto">
                    <Gift className="w-4 h-4 text-[#996515]" />
                  </div>
                  <p className="text-xs font-medium text-[#8B6914]">No Hidden Fees</p>
                </div>
                <div className="flex-shrink-0 bg-white/70 backdrop-blur-sm rounded-xl p-3 min-w-[120px] border border-[#B8860B]/30 text-center">
                  <div className="w-8 h-8 rounded-full bg-[#B8860B]/15 border border-[#B8860B]/30 flex items-center justify-center mb-1 mx-auto">
                    <CheckCircle className="w-4 h-4 text-[#996515]" />
                  </div>
                  <p className="text-xs font-medium text-[#8B6914]">Flexible Savings</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
